import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2, Clock } from "lucide-react";
import { PRICING } from "../../mock/mockData";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const parseEuro = (s) => Number(String(s).replace(/[^\d,.-]/g, "").replace(",", ".")) || 0;
const FALLBACK = PRICING.map((p) => ({
  name: p.name, highlight: p.highlight, desc: p.desc, features: p.features, cta: p.cta,
  base_m: parseEuro(p.price), base_y: parseEuro(p.price) * 12, promo: false, promo_m: 0, promo_y: 0, promoLabel: "", promoEnd: null,
}));

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offers, setOffers] = useState(null);
  const [busy, setBusy] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/offers").then((r) => {
      const list = (r.data.offers || []).map((o) => ({
        name: o.name,
        highlight: !!o.highlight,
        desc: o.desc || "",
        features: o.services || [],
        cta: o.cta || "Commencer",
        base_m: Number(o.price_monthly) || 0,
        base_y: Number(o.price_yearly) || 0,
        promo: !!o.promo_active,
        promo_m: Number(o.promo_price_monthly) || 0,
        promo_y: Number(o.promo_price_yearly) || 0,
        promoLabel: (o.promo_label || "").trim(),
        promoEnd: o.promo_end || null,
      }));
      setOffers(list.length ? list : FALLBACK);
    }).catch(() => setOffers(FALLBACK));
  }, []);

  const fmt = (n) => (n === 0 ? "0€" : `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: n % 1 ? 2 : 0 })}€`);
  const promoEndMs = (p) => (p.promoEnd ? new Date(p.promoEnd + "T23:59:59").getTime() : null);
  const promoLive = (p) => { const e = promoEndMs(p); return p.promo && (!e || e > now); };
  const countdown = (endMs) => {
    let s = Math.max(0, Math.floor((endMs - now) / 1000));
    const d = Math.floor(s / 86400); s %= 86400;
    const h = Math.floor(s / 3600); s %= 3600;
    const m = Math.floor(s / 60); s %= 60;
    return { d, h, m, s };
  };

  const handleChoose = async (p) => {
    // Offre gratuite ou visiteur non connecté → inscription/connexion
    if (p.isFree || !user) {
      if (!user && !p.isFree) {
        localStorage.setItem("pending_upgrade", JSON.stringify({ plan: p.name, cycle }));
      }
      navigate("/connexion");
      return;
    }
    setBusy(p.name);
    try {
      const res = await api.post("/auth/plan-checkout", { plan: p.name, cycle, origin_url: window.location.origin });
      if (res.data.free) { navigate("/app/profil"); setBusy(""); }
      else window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setBusy("");
    }
  };

  const plans = offers || FALLBACK;
  return (
    <section id="tarifs" className="py-24 bg-brand-softer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="text-brand-limeDark font-semibold mb-3">Tarifs</p>
          <h2 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep">Un prix pour chaque bailleur</h2>
          <p className="mt-4 text-lg text-brand-green/60">Commencez gratuitement, changez de formule quand vous voulez.</p>
        </div>

        {/* Sélecteur mensuel / annuel */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center bg-white border border-brand-soft rounded-full p-1" data-testid="cycle-toggle">
            <button
              onClick={() => setCycle("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${cycle === "monthly" ? "bg-brand-greenDeep text-white" : "text-brand-green/70 hover:text-brand-greenDeep"}`}
              data-testid="cycle-monthly"
            >Mensuel</button>
            <button
              onClick={() => setCycle("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${cycle === "yearly" ? "bg-brand-greenDeep text-white" : "text-brand-green/70 hover:text-brand-greenDeep"}`}
              data-testid="cycle-yearly"
            >Annuel <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cycle === "yearly" ? "bg-brand-lime text-brand-greenDeep" : "bg-brand-lime/25 text-brand-limeDark"}`}>2 mois offerts</span></button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {plans.map((p) => {
            const live = promoLive(p);
            const base = cycle === "yearly" ? p.base_y : p.base_m;
            const promoPrice = cycle === "yearly" ? p.promo_y : p.promo_m;
            const eff = live ? promoPrice : base;
            const isFree = eff === 0;
            const oldPrice = live && base > 0 && base !== promoPrice ? fmt(base) : null;
            const period = eff === 0 ? (live ? "pendant la promo" : "/ pour toujours") : (cycle === "yearly" ? "/ an" : "/ mois");
            const cd = live && promoEndMs(p) ? countdown(promoEndMs(p)) : null;
            return (
              <div
                key={p.name}
                className={`relative rounded-3xl p-8 flex flex-col ${p.highlight ? "bg-brand-greenDeep text-white shadow-2xl md:-translate-y-4" : "bg-white border border-brand-soft"}`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-lime text-brand-greenDeep text-xs font-bold">
                    <Sparkles className="w-3.5 h-3.5" /> Le plus populaire
                  </span>
                )}
                <h3 className={`font-display text-2xl font-semibold ${p.highlight ? "text-white" : "text-brand-greenDeep"}`}>{p.name}</h3>
                <p className={`text-sm mt-1 ${p.highlight ? "text-white/70" : "text-brand-green/60"}`}>{p.desc}</p>
                {live && (
                  <span className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-bold w-fit ${p.highlight ? "bg-brand-lime text-brand-greenDeep" : "bg-brand-lime/25 text-brand-limeDark"}`} data-testid={`pricing-promo-${p.name}`}>
                    <Sparkles className="w-3.5 h-3.5" /> {p.promoLabel || "Promotion"}
                  </span>
                )}
                <div className="mt-6 flex items-end gap-2">
                  {oldPrice && (
                    <span className={`font-display text-2xl font-semibold line-through ${p.highlight ? "text-white/50" : "text-brand-green/35"}`} data-testid={`pricing-oldprice-${p.name}`}>{oldPrice}</span>
                  )}
                  <span className={`font-display text-5xl font-bold ${p.highlight ? "text-white" : "text-brand-greenDeep"}`}>{fmt(eff)}</span>
                  <span className={`mb-2 text-sm ${p.highlight ? "text-white/60" : "text-brand-green/50"}`}>{period}</span>
                </div>
                {cd && (
                  <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg w-fit ${p.highlight ? "bg-white/10 text-brand-lime" : "bg-brand-lime/15 text-brand-limeDark"}`} data-testid={`pricing-countdown-${p.name}`}>
                    <Clock className="w-3.5 h-3.5" />
                    Offre valable encore {cd.d > 0 ? `${cd.d}j ` : ""}{String(cd.h).padStart(2, "0")}h {String(cd.m).padStart(2, "0")}m {String(cd.s).padStart(2, "0")}s
                  </div>
                )}
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${p.highlight ? "bg-brand-lime" : "bg-brand-soft"}`}>
                        <Check className="w-3 h-3 text-brand-greenDeep" strokeWidth={3} />
                      </span>
                      <span className={`text-sm ${p.highlight ? "text-white/85" : "text-brand-green/75"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleChoose({ name: p.name, isFree })}
                  disabled={busy === p.name}
                  className={`mt-8 w-full py-3.5 rounded-full font-semibold transition-colors disabled:opacity-60 ${p.highlight ? "bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep" : "bg-brand-soft hover:bg-brand-mint text-brand-greenDeep"}`}
                  data-testid={`pricing-cta-${p.name}`}
                >
                  {busy === p.name ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : p.cta}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
